import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, Chip, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, Button, Alert,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { authedFetch } from '../utils/authedFetch';

const FUNCTIONS_BASE = 'https://us-central1-torch-beach-app.cloudfunctions.net';

export default function TournamentMatchResults() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDiv, setActiveDiv] = useState(0);
  const [savingKey, setSavingKey] = useState(''); // "divName:place" while saving

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [resSnap, tSnap, regsSnap] = await Promise.all([
          getDoc(doc(db, 'tournamentResults', tid)),
          getDoc(doc(db, 'tournaments', tid)),
          getDocs(query(
            collection(db, 'registrations'),
            where('tournament_id', '==', tid),
            where('status', '==', 'active')
          )),
        ]);
        if (cancelled) return;
        if (!tSnap.exists()) { setError('Tournament not found.'); setLoading(false); return; }
        setTournament({ id: tSnap.id, ...tSnap.data() });
        if (resSnap.exists()) setResults({ id: resSnap.id, ...resSnap.data() });
        else setError('No results have been imported for this tournament yet.');
        setRegistrations(regsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tid]);

  // Build a fuzzy-match index for division registrations
  const regsByDivision = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const map = new Map();
    for (const r of registrations) {
      const k = norm(r.division);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return map;
  }, [registrations]);

  const candidatesForDivision = (divisionName) => {
    const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const exact = regsByDivision.get(norm(divisionName)) || [];
    if (exact.length > 0) return exact;
    return registrations; // fall back to all
  };

  async function saveMatch(divisionName, place, registrationId) {
    setSavingKey(`${divisionName}:${place}`);
    try {
      const res = await authedFetch(`${FUNCTIONS_BASE}/resolveTeamMatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournament_id: tid,
          division_name: divisionName,
          place,
          registration_id: registrationId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Update failed.'); return; }
      // Update local state
      setResults(prev => {
        const next = { ...prev };
        const div = { ...next.divisions[divisionName] };
        div.standings = div.standings.map(s =>
          (s.place === place || s.rank === place) ? data.standing : s
        );
        next.divisions = { ...next.divisions, [divisionName]: div };
        return next;
      });
    } catch (e) {
      alert('Update failed: ' + e.message);
    } finally {
      setSavingKey('');
    }
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')} sx={{ mt: 2 }}>
          Back to Tournament Admin
        </Button>
      </Container>
    );
  }

  const divisionNames = results ? Object.keys(results.divisions || {}) : [];

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">🔗 Match Results to Registrations</Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
            {tournament?.name || ''}
          </Typography>
        </Container>
      </Box>

      <Container sx={{ pb: 6 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Auto-matching connected vb-scores team names to registrations by first names.
          Review and resolve the unmatched and ambiguous ones below — these connect each result
          to the player records in your system, enabling player profile history and points totals.
        </Alert>

        {divisionNames.length === 0 ? (
          <Alert severity="warning">No divisions found.</Alert>
        ) : (
          <>
            <Tabs value={activeDiv} onChange={(_, v) => setActiveDiv(v)}
                  variant="scrollable" scrollButtons="auto"
                  sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              {divisionNames.map(name => {
                const standings = results.divisions[name].standings || [];
                const unresolved = standings.filter(s => s.match_status !== 'matched').length;
                return (
                  <Tab key={name}
                       label={
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           {name}
                           {unresolved > 0 && (
                             <Chip size="small" label={unresolved} color="warning"
                                   sx={{ height: 18, fontSize: '0.7rem' }} />
                           )}
                         </Box>
                       } />
                );
              })}
            </Tabs>

            {divisionNames.map((name, i) => {
              if (i !== activeDiv) return null;
              const div = results.divisions[name];
              const standings = div.standings || [];
              const candidates = candidatesForDivision(name);
              return (
                <Card key={name} elevation={2} sx={{ borderRadius: 3 }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" fontWeight="bold">{name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {candidates.length} registration{candidates.length !== 1 ? 's' : ''} in this division
                    </Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f4f6f9' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Place</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Team (vb-scores)</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Registration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {standings.map((s, idx) => {
                        const status = s.match_status || (s.registration_id ? 'matched' : 'unmatched');
                        const saving = savingKey === `${name}:${s.place}`;
                        return (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: 600 }}>{s.place}</TableCell>
                            <TableCell>{s.team_name}</TableCell>
                            <TableCell>
                              {status === 'matched' ? (
                                <Chip icon={<CheckCircleIcon />} label={s.manually_matched ? 'Manual' : 'Auto'}
                                      color="success" size="small" />
                              ) : status === 'ambiguous' ? (
                                <Chip icon={<WarningAmberIcon />} label="Ambiguous" color="warning" size="small" />
                              ) : (
                                <Chip icon={<HelpOutlineIcon />} label="Unmatched" size="small" />
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 320 }}>
                              <FormControl fullWidth size="small" disabled={saving}>
                                <Select value={s.registration_id || ''}
                                        onChange={e => saveMatch(name, s.place, e.target.value)}
                                        displayEmpty>
                                  <MenuItem value="">— Unmatched —</MenuItem>
                                  {candidates.map(r => (
                                    <MenuItem key={r.id} value={r.id}>
                                      {r.first_name} {r.last_name}
                                      {r.partner_first_name ? ` & ${r.partner_first_name} ${r.partner_last_name || ''}` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {saving && <Typography variant="caption" color="text.secondary">Saving...</Typography>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              );
            })}
          </>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')} variant="outlined">
            Back to Tournament Admin
          </Button>
          <Button component={Link} to={`/tournament/${tid}/results`} variant="outlined" color="primary">
            View Public Results Page
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
